import type { AccountMapping } from 'further-types/lp/account';

/** Fields required to evaluate partial-row visibility for account mapping UI. */
export type AccountMappingPartialRowInput = Pick<
  AccountMapping,
  | 'actionRef'
  | 'actionType'
  | 'transactionSubType'
  | 'debitTransactionTypeId'
  | 'creditTransactionTypeId'
>;
import { ActionRef, ActionTypes } from 'further-types/lp/action';
import {
  BankReconciliationActionType,
  CurrencySaleRevaluationType,
} from 'further-types/lp/bank-reconciliation';
import { InvestmentTypes } from 'further-types/lp/investment';
import { RealisationTransactionSubType } from 'further-types/lp/realisation';
import { RevaluationTransactionType } from 'further-types/lp/revaluation';
import { RealisedFXGainOrLossType } from 'further-types/lp/realised-fx-gain-or-loss';
import { UnrealisedAssetFXGainOrLossType } from 'further-types/lp/unrealised-asset-fx-gain-or-loss';
import { UnrealisedCashFXGainOrLossType } from 'further-types/lp/unrealised-cash-fx-gain-or-loss';
import { UnrealisedFXGainOrLossSubType } from 'further-types/lp/unrealised-fx-gain-or-loss';

const hiddenTransactionSubTypes = new Set<AccountMapping['transactionSubType']>(
  [
    RealisationTransactionSubType.RealisedLoss,
    RealisationTransactionSubType.RealisedLossFX,
    RealisedFXGainOrLossType.RealisedFXLoss,
    UnrealisedAssetFXGainOrLossType.UnrealisedAssetFXLoss,
    UnrealisedCashFXGainOrLossType.UnrealisedCashFXLoss,
    UnrealisedFXGainOrLossSubType.FXLossPayable,
    UnrealisedFXGainOrLossSubType.FXLossReceivable,
  ],
);

/**
 * Rows that are the hidden “opposite” of an inferred gain/tooltip row.
 * They stay in the form payload so saves do not drop required API mappings.
 */
export function isHiddenAccountMappingRow(row: AccountMapping): boolean {
  if (row.actionType === RevaluationTransactionType.CompanyRevaluationDown) {
    return true;
  }
  if (row.actionType === CurrencySaleRevaluationType.CurrencySaleLoss) {
    return true;
  }
  if (row.actionType === InvestmentTypes.InvestmentRealisedCurrencyLoss) {
    return true;
  }
  return hiddenTransactionSubTypes.has(row.transactionSubType);
}

export function isPartialAccountMappingRow(
  row: AccountMappingPartialRowInput,
): boolean {
  const hasDebit = row.debitTransactionTypeId != null;
  const hasCredit = row.creditTransactionTypeId != null;
  return (hasDebit && !hasCredit) || (!hasDebit && hasCredit);
}

/**
 * Partial rows that belong in the dedicated “FX on reconciliation” block
 * (per product spec), not under general FX or other categories.
 */
export function isFxOnReconciliationPartialRow(
  row: AccountMappingPartialRowInput,
): boolean {
  if (!isPartialAccountMappingRow(row)) {
    return false;
  }
  if (row.actionRef === ActionRef.UnrealisedFXGainOrLoss) {
    return true;
  }
  if (row.actionRef === ActionRef.UnrealisedCashFXGainOrLoss) {
    return true;
  }
  if (row.actionRef !== ActionRef.Reconciliation) {
    return false;
  }
  if (row.actionType !== BankReconciliationActionType.ReconciliationCreated) {
    return false;
  }
  return row.transactionSubType === RealisedFXGainOrLossType.RealisedFXGain;
}

const fxReconciliationPartialSortOrder: Array<{
  actionRef: ActionRef;
  actionType: AccountMapping['actionType'];
  transactionSubType: AccountMapping['transactionSubType'];
}> = [
  {
    actionRef: ActionRef.UnrealisedCashFXGainOrLoss,
    actionType: ActionTypes.UnrealisedCashFXGainOrLossCreated,
    transactionSubType: UnrealisedCashFXGainOrLossType.UnrealisedCashFXGain,
  },
  {
    actionRef: ActionRef.UnrealisedFXGainOrLoss,
    actionType: ActionTypes.FXGainOrLoss,
    transactionSubType: UnrealisedFXGainOrLossSubType.FXGainPayable,
  },
  {
    actionRef: ActionRef.UnrealisedFXGainOrLoss,
    actionType: ActionTypes.FXGainOrLoss,
    transactionSubType: UnrealisedFXGainOrLossSubType.FXGainReceivable,
  },
  {
    actionRef: ActionRef.Reconciliation,
    actionType: BankReconciliationActionType.ReconciliationCreated,
    transactionSubType: RealisedFXGainOrLossType.RealisedFXGain,
  },
];

export function fxOnReconciliationPartialSortIndex(
  row: AccountMappingPartialRowInput,
): number {
  const idx = fxReconciliationPartialSortOrder.findIndex(
    (key) =>
      key.actionRef === row.actionRef &&
      key.actionType === row.actionType &&
      key.transactionSubType === row.transactionSubType,
  );
  return idx === -1 ? fxReconciliationPartialSortOrder.length : idx;
}
